import { useEffect, useRef, useState, useCallback } from "react";
import { http } from "./api";

const KEY_ENABLED = "reminders_enabled";
const KEY_FIRED = (date) => `reminders_fired_${date}`;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function loadFiredSet() {
  try {
    const raw = localStorage.getItem(KEY_FIRED(todayISO()));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (err) {
    console.warn("Failed to load reminders fired-set:", err);
    return new Set();
  }
}
function saveFiredSet(set) {
  try { localStorage.setItem(KEY_FIRED(todayISO()), JSON.stringify([...set])); }
  catch (err) { console.warn("Failed to persist reminders fired-set:", err); }
}

export function useHabitReminders(username) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(KEY_ENABLED) === "1");
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const timerRef = useRef(null);

  const toggle = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    if (!enabled) {
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return;
      localStorage.setItem(KEY_ENABLED, "1");
      setEnabled(true);
    } else {
      localStorage.setItem(KEY_ENABLED, "0");
      setEnabled(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !username || typeof Notification === "undefined" || Notification.permission !== "granted") {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }

    async function tick() {
      try {
        const { data } = await http.get(`/habits`, { params: { date: todayISO(), owner: username } });
        const now = nowHHMM();
        const fired = loadFiredSet();
        for (const h of data.habits) {
          if (!h.expected_time) continue;
          if (h.log?.status === "full") continue;
          if (fired.has(h.id)) continue;
          // Fire if current time matches expected within the last 60s window
          if (now === h.expected_time) {
            try {
              new Notification(`⏰ ${h.name}`, {
                body: `It's time for ${h.name}. Full = ${h.full} pts.`,
                tag: `habit-${h.id}-${todayISO()}`,
              });
            } catch (err) { console.warn("Notification creation failed:", err); }
            fired.add(h.id);
          }
        }
        saveFiredSet(fired);
      } catch (err) { console.warn("Reminder tick failed:", err); }
    }
    tick();
    timerRef.current = setInterval(tick, 60_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [enabled, username]);

  return { enabled, permission, toggle };
}
